import { ArrowLeft } from "lucide-react";
import { useRef, useState } from "react";
import type { DashboardDetailResponse } from "@/features/dashboard/types";
import {
  DashboardDetailContent,
  type DashboardDetailContentProps,
} from "@/components/dashboard/DashboardDetailContent";

const DETAIL_DRAWER_CSS = `
  .dashboard-ledger-detail {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 0.75rem;
    min-height: 0;
    min-width: 0;
    overflow: hidden;
  }

  .dashboard-ledger-detail__back {
    align-items: center;
    align-self: flex-start;
    background: transparent;
    border: 0;
    border-radius: 0.4rem;
    color: var(--login-accent);
    cursor: pointer;
    display: inline-flex;
    flex: 0 0 auto;
    font-family: "DM Sans", sans-serif;
    font-size: 0.84rem;
    font-weight: 800;
    gap: 0.4rem;
    min-height: 44px;
    padding: 0 0.45rem;
    transition:
      background-color 140ms ease,
      color 140ms ease,
      transform 120ms ease;
  }

  .dashboard-ledger-detail__back:hover {
    background: color-mix(in srgb, var(--login-accent) 8%, var(--login-surface));
  }
  .dashboard-ledger-detail__back:focus-visible {
    outline: 2px solid var(--login-accent);
    outline-offset: 2px;
  }
  .dashboard-ledger-detail__back:active { transform: translateY(1px); }

  .dashboard-ledger-detail__content {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
    min-width: 0;
    overflow: hidden;
  }

  @media (max-width: 390px) {
    .dashboard-ledger-detail { gap: 0.55rem; }
    .dashboard-ledger-detail__back { min-height: 44px; }
  }

  @media (prefers-reduced-motion: reduce) {
    .dashboard-ledger-detail__back {
      transition-duration: 0.01ms;
      transform: none !important;
    }
  }
`;

export interface DashboardDetailDrawerProps extends Omit<
  DashboardDetailContentProps,
  "pages"
> {
  pages?: readonly DashboardDetailResponse[];
  onBack: () => void;
}

export function DashboardDetailDrawer({
  onBack,
  pages,
  isFetchingNextPage,
  onLoadMore,
  ...detailProps
}: DashboardDetailDrawerProps) {
  const requestRef = useRef<Promise<unknown> | null>(null);
  const [localPagePending, setLocalPagePending] = useState(false);
  const pagePending = isFetchingNextPage || localPagePending;
  const requestNextPage = () => {
    if (pagePending || requestRef.current) return;
    setLocalPagePending(true);
    const request = Promise.resolve(onLoadMore());
    requestRef.current = request;
    void request.finally(() => {
      if (requestRef.current !== request) return;
      requestRef.current = null;
      setLocalPagePending(false);
    });
  };

  return (
    <div className="dashboard-ledger-detail">
      <style>{DETAIL_DRAWER_CSS}</style>
      <button
        type="button"
        className="dashboard-ledger-detail__back"
        autoFocus
        onClick={onBack}
      >
        <ArrowLeft aria-hidden="true" size={17} />
        Voltar
      </button>
      <div
        className="dashboard-ledger-detail__content"
        data-dashboard-scroll-owner="detail"
      >
        <DashboardDetailContent
          {...detailProps}
          pages={pages}
          isFetchingNextPage={pagePending}
          onLoadMore={requestNextPage}
        />
      </div>
    </div>
  );
}
